import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import {
  RCS_BASIC_MAX_LENGTH,
  RCS_TEXT_MAX_LENGTH,
  RCS_BUTTON_LABEL_MAX,
  RCS_BUTTON_TYPES,
  RCS_STAGE_LABELS,
  RCS_USE_CASES,
  RcsAgent,
  RcsButton,
  RcsButtonType,
  RcsContent,
  RcsMedia,
  RcsMessageType,
  RcsPreview,
} from 'src/app/models/Rcs';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';
import { AccountVersionService } from 'src/app/shared/services/account-version.service';
import { RcsAgentService } from 'src/app/shared/services/rcs-agent.service';
import { RcsBroadcastService } from 'src/app/shared/services/rcs-broadcast.service';
import {
  brandInitial,
  buildFallbackSms,
  classifyContent,
  contentProblems,
  emptyContent,
  normalizeContent,
  RcsClassification,
  resolveExample,
} from 'src/app/shared/utils/rcs-rules';
import { RcsGalleryModalComponent } from '../rcs-gallery-modal/rcs-gallery-modal.component';

/** Variável que o cliente pode inserir no texto. `tag` sem as chaves. */
export interface RcsVariable {
  tag: string;
  label: string;
}

/** Variáveis do envio em massa para lista (as mesmas do SMS Massivo). */
export const RCS_BROADCAST_VARIABLES: RcsVariable[] = [
  { tag: 'first_name', label: 'Primeiro nome do lead' },
  { tag: 'email', label: 'E-mail do lead' },
  { tag: 'list', label: 'Nome da lista' },
  { tag: 'campaign', label: 'Nome do envio' },
  { tag: 'dia_hj', label: 'Dia de hoje' },
];

/**
 * Variáveis do catálogo da automação que são link, na ordem dos chips do campo Link.
 * Nome, e-mail, datas, cupom, linha digitável e código Pix ficam de fora: não são URL.
 */
export const RCS_LINK_VARIABLE_TAGS: string[] = [
  'url_checkout',
  'url_checkout_cross',
  'url_pix',
  'url_boleto',
  'tracking_url',
  'url_membros',
  'url_lista',
];

/** Estado que o editor entrega a quem o usa, para travar o salvar e montar o custo. */
export interface RcsEditorState {
  type: RcsMessageType;
  reasons: string[];
  smsText: string;
  problems: string[];
  agentActive: boolean;
  /** `true` quando formato e SMS vieram do `POST /api/rcs/preview`. */
  fromServer: boolean;
}

/** Tempo sem digitar antes de pedir a prévia ao backend. */
export const RCS_PREVIEW_DEBOUNCE_MS = 400;

const BUTTON_PLACEHOLDERS: Record<RcsButtonType, string> = {
  url: 'https://… ou {url_checkout}',
  call: '+55 11 4000-1234',
  pix: '{pix_code} ou o código',
};

/**
 * Editor da mensagem RCS, o mesmo no envio em massa e na etapa da automação.
 *
 * O cliente escreve UMA mensagem; o formato (Basic ou Single) é detectado pelo
 * conteúdo e o SMS de quem não tem RCS é gerado a partir dela. A regra local dá
 * retorno na hora; a resposta do `POST /api/rcs/preview` substitui a local quando
 * chega, porque é a regra do backend que decide a cobrança.
 */
@Component({
  selector: 'app-rcs-editor',
  templateUrl: './rcs-editor.component.html',
})
export class RcsEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() agentId: string | null = null;
  @Output() agentIdChange = new EventEmitter<string | null>();

  @Input() content: RcsContent = emptyContent();
  @Output() contentChange = new EventEmitter<RcsContent>();

  @Input() variables: RcsVariable[] = RCS_BROADCAST_VARIABLES;

  /** Muda o texto do aviso de agente parado: envio não sai, etapa fica pausada. */
  @Input() context: 'broadcast' | 'sequence' = 'broadcast';

  @Output() stateChange = new EventEmitter<RcsEditorState>();

  /**
   * Chips "Links disponíveis" do botão "Abrir link": as variáveis de URL que a automação
   * oferece. Só na etapa da automação; no envio em massa a lista não tem link variável.
   */
  linkVariables: RcsVariable[] = [];

  /** Variáveis de URL pertencem ao campo Link, não ao texto da mensagem. */
  get textVariables(): RcsVariable[] {
    return (this.variables || []).filter(variable => !RCS_LINK_VARIABLE_TAGS.includes(variable.tag) && variable.tag !== 'pix_code');
  }

  /** Código Pix copiável é variável do botão, não URL nem texto do card. */
  get pixVariables(): RcsVariable[] {
    return this.context === 'sequence' ? (this.variables || []).filter(variable => variable.tag === 'pix_code') : [];
  }

  /** Destino de cada botão "Abrir link" na prévia, com o valor de exemplo. */
  previewLinks: Array<{ label: string; url: string }> = [];

  @ViewChild('textArea') textArea: ElementRef<HTMLTextAreaElement>;

  readonly buttonTypes = RCS_BUTTON_TYPES;
  readonly buttonLabelMax = RCS_BUTTON_LABEL_MAX;
  readonly basicMax = RCS_BASIC_MAX_LENGTH;
  readonly textMax = RCS_TEXT_MAX_LENGTH;
  readonly stageLabels = RCS_STAGE_LABELS;

  agents: RcsAgent[] = [];
  /** Opções do select com busca: só o agente Ativo pode ser escolhido; os outros aparecem travados, com a etapa. */
  agentOptions: SearchSelectOption[] = [];
  loadingAgents = true;
  agentsFailed = false;

  /** Cópia de trabalho. O botão 2 só aparece quando o cliente o adiciona (ou o conteúdo já vem com ele). */
  title = '';
  mediaUrl: string | null = null;
  text = '';
  rows: RcsButton[] = [this.blankButton(), this.blankButton('pix')];
  showSecondButton = false;

  view: 'rcs' | 'sms' = 'rcs';
  local: RcsClassification = classifyContent(emptyContent());
  localSms = '';
  preview: RcsPreview | null = null;
  previewFailed = false;
  /** Motivo do 422 do `/preview`: o servidor respondeu e recusou o conteúdo (não é queda). */
  previewError: string | null = null;
  problems: string[] = [];

  private lastEmitted: RcsContent | null = null;
  private previewRequests = new Subject<void>();
  private subscriptions: Subscription[] = [];

  constructor(
    private agentService: RcsAgentService,
    private broadcastService: RcsBroadcastService,
    private modalService: NgbModal,
    private accountVersionService: AccountVersionService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.previewRequests.pipe(
        debounceTime(RCS_PREVIEW_DEBOUNCE_MS),
        switchMap(() => {
          const content = this.currentContent();
          return this.broadcastService.preview(this.agentId, content).pipe(
            map(preview => ({ preview, content, error: null as string | null })),
            catchError(err => of({ preview: null as RcsPreview, content, error: rejectionMessage(err) })),
          );
        }),
      ).subscribe(({ preview, content, error }) => {
        // Resposta de um conteúdo que já mudou não vale: a próxima vem aí.
        if (JSON.stringify(content) !== JSON.stringify(this.currentContent())) {
          return;
        }
        this.preview = preview;
        this.previewFailed = !preview;
        this.previewError = error;
        this.emitState();
      })
    );

    this.loadAgents();
    this.linkVariables = this.pickLinkVariables();
    // Primeiro estado fora do ciclo de detecção do pai: emitir dentro do
    // `ngOnInit` mudaria um binding dele no meio da checagem.
    this.refresh(false, false);
    Promise.resolve().then(() => this.emitState());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.variables || changes.context) {
      this.linkVariables = this.pickLinkVariables();
    }

    if (changes.content && this.content !== this.lastEmitted) {
      this.adopt(this.content || emptyContent());
      if (!changes.content.firstChange) {
        this.refresh(false);
      }
    }

    if (changes.agentId && !changes.agentId.firstChange) {
      this.requestPreview();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  loadAgents(): void {
    this.loadingAgents = true;
    this.agentsFailed = false;
    this.subscriptions.push(
      this.agentService.list().subscribe({
        next: agents => {
          this.agents = agents || [];
          this.agentOptions = this.agents.map(agent => ({
            value: agent.id,
            label: agent.name,
            hint: agent.stage === 'active' ? this.useCaseLabel(agent) : this.stageLabels[agent.stage],
            disabled: agent.stage !== 'active',
          }));
          this.loadingAgents = false;

          // Sem agente escolhido e com um ativo à mão, o primeiro ativo vem marcado.
          if (!this.agentId && this.activeAgents.length) {
            this.selectAgent(this.activeAgents[0].id);
          } else {
            this.emitState();
          }
        },
        error: () => {
          this.loadingAgents = false;
          this.agentsFailed = true;
          this.emitState();
        },
      })
    );
  }

  get activeAgents(): RcsAgent[] {
    return this.agents.filter(agent => agent.stage === 'active');
  }

  get selectedAgent(): RcsAgent | null {
    return this.agents.find(agent => agent.id === this.agentId) || null;
  }

  get agentActive(): boolean {
    return this.selectedAgent?.stage === 'active';
  }

  useCaseLabel(agent: RcsAgent): string {
    return RCS_USE_CASES.find(item => item.value === agent.use_case)?.label || '';
  }

  initial(name: string): string {
    return brandInitial(name);
  }

  selectAgent(id: string | null): void {
    this.agentId = id || null;
    this.agentIdChange.emit(this.agentId);
    this.requestPreview();
    this.emitState();
  }

  // --- Conteúdo ---------------------------------------------------------------

  onChange(): void {
    this.refresh(true);
  }

  insertVariable(variable: RcsVariable): void {
    const tag = `{${variable.tag}}`;
    const element = this.textArea?.nativeElement;
    const start = element ? element.selectionStart : this.text.length;
    const end = element ? element.selectionEnd : this.text.length;
    const before = this.text.slice(0, start);
    const needsSpace = before && !/\s$/.test(before) ? ' ' : '';

    this.text = before + needsSpace + tag + this.text.slice(end);
    this.refresh(true);

    if (element) {
      const caret = start + needsSpace.length + tag.length;
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(caret, caret);
      });
    }
  }

  /** O destino de um botão é único: o chip substitui o valor manual ou outra variável. */
  insertButtonVariable(row: RcsButton, variable: RcsVariable, input?: HTMLInputElement): void {
    const tag = `{${variable.tag}}`;
    row.value = tag;
    this.refresh(true);

    if (input) {
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(tag.length, tag.length);
      });
    }
  }

  openGallery(): void {
    const modal = this.modalService.open(RcsGalleryModalComponent, {
      size: 'xl',
      scrollable: true,
      // O modal vive no `<body>`, fora do escopo de versão do shell.
      windowClass: `rcs-modal ${this.accountVersionService.themeClass}`,
    });
    modal.componentInstance.selectedUrl = this.mediaUrl;
    modal.result.then(
      (media: RcsMedia | null) => {
        this.mediaUrl = media ? media.url : null;
        this.refresh(true);
      },
      () => undefined,
    );
  }

  removeMedia(): void {
    this.mediaUrl = null;
    this.refresh(true);
  }

  /** "+ Adicionar botão": mostra o botão 2, vazio. */
  addSecondButton(): void {
    this.rows[1] = this.blankButton('pix');
    this.showSecondButton = true;
    this.refresh(true);
  }

  /** "Remover": limpa o botão 2 e o esconde; ele sai da prévia, da validação e do conteúdo. */
  removeSecondButton(): void {
    this.rows[1] = this.blankButton('pix');
    this.showSecondButton = false;
    this.refresh(true);
  }

  /** Linhas na tela: o botão 1 sempre; o 2 só depois de adicionado. */
  get visibleRows(): RcsButton[] {
    return this.showSecondButton ? this.rows : this.rows.slice(0, 1);
  }

  buttonValueLabel(type: RcsButtonType): string {
    return RCS_BUTTON_TYPES.find(item => item.value === type)?.valueLabel || 'Valor';
  }

  buttonPlaceholder(type: RcsButtonType): string {
    return BUTTON_PLACEHOLDERS[type];
  }

  buttonIcon(type: RcsButtonType): string {
    return RCS_BUTTON_TYPES.find(item => item.value === type)?.icon || 'fa-hand-pointer-o';
  }

  // --- Prévia -----------------------------------------------------------------

  get type(): RcsMessageType {
    return this.preview?.type || this.local.type;
  }

  get reasons(): string[] {
    return this.preview?.reasons || this.local.reasons;
  }

  get resolvedText(): string {
    return this.preview ? this.preview.resolved_text : this.local.resolvedText;
  }

  get textLength(): number {
    return this.preview ? this.preview.text_length : this.local.resolvedText.length;
  }

  get smsText(): string {
    return this.preview ? this.preview.sms_text : this.localSms;
  }

  get smsLength(): number {
    return this.preview ? this.preview.sms_length : this.localSms.length;
  }

  /** Botões com rótulo, na ordem do card. */
  get cardButtons(): RcsButton[] {
    return this.visibleRows.filter(button => (button.label || '').trim());
  }

  setView(view: 'rcs' | 'sms'): void {
    this.view = view;
  }

  // --- Interno ----------------------------------------------------------------

  private blankButton(type: RcsButtonType = 'url'): RcsButton {
    return { type, label: '', value: '' };
  }

  private pickLinkVariables(): RcsVariable[] {
    if (this.context !== 'sequence') {
      return [];
    }
    const available = this.variables || [];
    return RCS_LINK_VARIABLE_TAGS
      .map(tag => available.find(variable => variable.tag === tag))
      .filter((variable): variable is RcsVariable => !!variable);
  }

  private adopt(content: RcsContent): void {
    this.title = content.title || '';
    this.mediaUrl = content.media_url || null;
    this.text = content.text || '';
    const buttons = content.buttons || [];
    this.rows = [0, 1].map(index => buttons[index]
      ? { type: buttons[index].type, label: buttons[index].label || '', value: buttons[index].value || '' }
      : this.blankButton(index === 1 ? 'pix' : 'url'));
    // Conteúdo salvo com dois botões (edição) já abre com o botão 2 à mostra.
    this.showSecondButton = !!((this.rows[1].label || '').trim() || (this.rows[1].value || '').trim());
  }

  private currentContent(): RcsContent {
    return normalizeContent({ title: this.title, media_url: this.mediaUrl, text: this.text, buttons: this.visibleRows });
  }

  /** Recalcula a regra local e, se o cliente mexeu, avisa o pai e pede a prévia ao backend. */
  private refresh(emit: boolean, announce = true): void {
    const draft: RcsContent = { title: this.title, media_url: this.mediaUrl, text: this.text, buttons: this.visibleRows };
    this.local = classifyContent(draft);
    this.localSms = buildFallbackSms(draft, this.local);
    this.problems = contentProblems(draft);
    this.previewLinks = this.cardButtons
      .filter(button => button.type === 'url' && (button.value || '').trim())
      .map(button => ({ label: button.label.trim(), url: resolveExample(button.value.trim()) }));

    // A prévia do backend era do conteúdo anterior: sai da tela até a nova chegar.
    this.preview = null;
    this.previewFailed = false;
    this.previewError = null;

    if (emit) {
      this.lastEmitted = this.currentContent();
      this.contentChange.emit(this.lastEmitted);
    }

    this.requestPreview();
    if (announce) {
      this.emitState();
    }
  }

  private requestPreview(): void {
    if ((this.text || '').trim()) {
      this.previewRequests.next();
    }
  }

  private emitState(): void {
    const problems = [...this.problems];
    if (!this.loadingAgents && !this.agentActive) {
      problems.unshift(this.activeAgents.length ? 'Escolha um agente ativo.' : 'Nenhum agente ativo: cadastre e aguarde a aprovação.');
    }

    this.stateChange.emit({
      type: this.type,
      reasons: this.reasons,
      smsText: this.smsText,
      problems,
      agentActive: this.agentActive,
      fromServer: !!this.preview,
    });
  }
}

/**
 * Texto do 422 do `/preview` (primeiro erro por campo, senão `message`). Outros códigos
 * e queda de rede voltam `null`: aí a prévia é do navegador porque o servidor não respondeu.
 */
export function rejectionMessage(err: any): string | null {
  if (!err || err.status !== 422 || !err.error) {
    return null;
  }
  const errors = err.error.errors;
  if (errors && typeof errors === 'object') {
    const first = ([] as unknown[]).concat(...Object.values(errors)).find(item => typeof item === 'string');
    if (first) {
      return first as string;
    }
  }
  return typeof err.error.message === 'string' ? err.error.message : null;
}
